<template>
  <nav id="nav--container" class="nav--container" :class="{ 'nav--container-is_open': isOpen }" @click.stop>
    <a
      class="material-icons nav--handle"
      href="#"
      @click.prevent.stop="handleClick"
      :aria-label="isOpen ? 'Close Menu' : 'Click to Open Menu, Press ESC to Close'"
      :aria-expanded="isOpen"
      aria-controls="nav--container"
      >menu</a
    >

    <div class="nav--scroller">
      <router-link class="nav--title" to="/" @click="isOpen = false" title="View Homepage">
        <Logo aria-label="Online Players Club" />
      </router-link>
      <div v-if="badge" class="nav--badge">
        <peppermill-badge :label="badge.label" :icon="badge.icon" variant="success" mod="subtle" is-small></peppermill-badge>
      </div>
      <!--      <img v-if="-1 !== ['01','04','07'].indexOf(appStore.userPropertyId || '')" class="nav&#45;&#45;property_logo"
           alt="Property Logo"
           :src="appStore.assetUrl + 'library/images/property-logos/' + appStore.userPropertyId +  '.png'"/>-->
      <transition name="nav_fade">
        <ul class="nav--links" v-if="navLinks.length">
          <li v-for="link in navLinks" :key="link.path" :class="link.classes">
            <router-link
              :to="link.path"
              :title="link.title"
              :class="{ 'nav--link-icon': link.icon.length }"
              @click="isOpen = false"
            >
              <template v-if="link.icon.length"
                ><span class="material-icons">{{ link.icon }}</span></template
              >
              {{ link.title }}
            </router-link>
          </li>
        </ul>
      </transition>

      <div class="nav--bottom_links">
        <router-link :to="{ name: 'contact' }" title="View the contact form" @click="isOpen = false"
          >Contact Us</router-link
        >
        <a :href="siteLinks.accessibility" target="_blank" title="View accessibility form in new window"
          >Accessibility</a
        >
        <!--        <router-link to="accessibility" @click="isOpen = false">Accessibility</router-link>-->
        <a :href="siteLinks.privacy" target="_blank" title="View privacy policy in new window">Privacy Policy</a>
      </div>
    </div>
  </nav>
  <div class="nav--overlay"></div>
</template>

<script setup lang="ts">
/**
 * <peppermill-app-navigation> handles nav UX for <peppermill-app-container>.
 *
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version 1.5.0
 * @changelog
 *  2023-01-16 (1.0.0) (jhoward)
 *    - <peppermill-app-navigation> displays the app name, property logo, current user's navigation buttons, current workstation and current user.
 *
 * 2026-02-09 (1.0.1) (jhoward)
 *   - Update <router-link> in App\Components\Layout\PeppermillNavigation to set isOpen = false on click, ensuring navigation closes when app title is clicked.
 *
 * 2026-03-11 (1.1.0) (jhoward)
 *   - Add AppNavigation.vue for app navigation and nav links.
 *
 * 2026-03-25 (1.2.0) (jhoward)
 *   - Add bottom navigation links for Contact Us, Accessibility, and Privacy Policy.
 *   - Adjust styles for nav--bottom_links.
 *
 * 2026-03-30 (1.3.0) (jhoward)
 *   - Add bottom navigation links for Contact Us, Accessibility, and Privacy Policy.
 *   - Improve accessibility with aria-labels and aria-controls.
 *   - Adjust styles for nav--bottom_links.
 *
 * 2026-04-09 (1.4.0) (jhoward)
 *   - Add logo-passport-rewards.svg to navigation title.
 *   - Refactor navigation markup for improved accessibility and style.
 *   - Update navigation links with descriptive titles and improved structure.
 *
 * 2026-10-09 (1.5.0) (jhoward)
 *   - Restyle the drawer as a Material Design 3 navigation drawer, from the framework's M3 tokens: 360px wide on the
 *     surface-container-low role, 56px pill destinations (48px for sub-destinations) with a secondary-container
 *     active indicator and filled icon, state layers on hover, a focus ring, and the M3 scrim and motion.
 *   - Dock the drawer permanently from 1568px, not 1401px. The framework's .layout--wrapper makes room for it from
 *     1568px, so between 1401px and 1567px the drawer used to sit over the page.
 *   - Keep the Contact Us / Accessibility / Privacy Policy links sticky at the foot of the scroller instead of
 *     absolutely positioned over it, so a short window no longer hides the last destinations behind them.
 *   - Stop the title link shrinking inside the now-flex scroller, which would otherwise crop the logo.
 *   - Point Contact Us, Accessibility and Privacy Policy at the current property's marketing site
 *     (helpers/getPropertySiteLinks) instead of wendoverfun.com for everyone.
 *
 * 2026-10-10 (1.6.0) (jhoward)
 *   - Add an optional badge under the logo, for the "Protected" status. No username is drawn anywhere in the chrome,
 *     so this is where a status about the account can sit.
 *
 * 2026-10-10 (1.7.0) (jhoward)
 *   - Contact Us opens the app's own /contact page instead of the marketing site's contact form in a new window.
 *     Accessibility and Privacy Policy still go to the current property's site.
 */

import PeppermillBadge from '@framework/components/PeppermillBadge.vue';
import type { PeppermillUserNavLink } from '@framework/defs/user';
import type { PropType } from 'vue';
import { computed, onMounted, ref, watch } from 'vue';
import { RouterLink, useRouter } from 'vue-router';
import Logo from '@/assets/logos/logo-passport-rewards.svg';
import getPropertySiteLinks from '@/helpers/getPropertySiteLinks';
import useAppStore from '@/store';

export interface NavBadge {
  label: string;
  icon: string;
}

interface NavLink extends PeppermillUserNavLink {
  classes: { [key: string]: boolean };
}
const props = defineProps({
  links: {
    type: Array as PropType<PeppermillUserNavLink[] | null>,
    default: null,
  },
  badge: {
    type: Object as PropType<NavBadge | null>,
    default: null,
  },
});

const appStore = useAppStore();
//The marketing site of the property the guest is on, not one fixed site: the store knows it before /state returns.
const siteLinks = computed(() => getPropertySiteLinks(appStore.defaultProperty));
const isOpen = ref<boolean>(false);
const htmlNode = ref<HTMLElement | null>(null);
const router = useRouter();

function handleClick() {
  isOpen.value = true;
  setTimeout(() => {
    document.querySelector<HTMLDivElement>('#nav--container a')?.focus();
  }, 500);
}

onMounted(() => {
  htmlNode.value = document.querySelector('html');
  document.body.addEventListener('click', () => {
    isOpen.value = false;
  });
  document.body.addEventListener('keyup', (event) => {
    if ('Escape' === event.key) {
      isOpen.value = false;
    }
  });
});

watch(isOpen, () => {
  if (htmlNode.value) {
    if (isOpen.value) {
      htmlNode.value.classList.add('layout-nav_open');
    } else {
      htmlNode.value.classList.remove('layout-nav_open');
    }
  }
});

const navLinks = computed<NavLink[]>(() => {
  const output = props.links as NavLink[];
  return output.map((link, linkIndex) => {
    link.classes = {
      'nav--link-sub': link.isSub,
      'nav--link-has_sub': !link.isSub && output.length > linkIndex + 1 && output[linkIndex + 1].isSub,
      'nav--link-first_sub': link.isSub && 0 < linkIndex && !output[linkIndex - 1].isSub,
      'nav--link-last_sub': link.isSub && output.length > linkIndex + 1 && !output[linkIndex + 1].isSub,
      'nav--link-active': link.path === router.currentRoute.value.path,
    };
    return link;
  }) as NavLink[];
});
</script>

<style scoped>
/*
 * Material Design 3 navigation drawer: a modal drawer below 1568px, permanently docked from 1568px up - the width at
 * which the framework's .layout--wrapper makes room for it (framework-layout.css). The tokens come from the
 * framework's peppermill.css, imported in main.ts.
 */
.nav--container {
  --nav-drawer-width: 360px;
  position: fixed;
  display: flex;
  flex-direction: column;
  top: 0;
  left: 0;
  bottom: 0;
  width: var(--nav-drawer-width);
  max-width: calc(100vw - 56px);
  box-sizing: border-box;
  z-index: 30;
  background-color: var(--md-sys-color-surface-container-low);
  border-radius: 0 var(--md-sys-shape-corner-large) var(--md-sys-shape-corner-large) 0;
  will-change: transform;
  transform: translateX(-100%);
  transition: transform var(--md-sys-motion-duration-medium1) var(--md-sys-motion-easing-standard);
}

.layout-nav_open .nav--container {
  transform: none;
}

.nav--handle {
  position: fixed;
  top: 8px;
  right: -60px;
  width: 48px;
  height: 48px;
  font-size: 48px;
  color: #fff;
  z-index: 25;
  text-decoration: none;
  transition: opacity var(--md-sys-motion-duration-short2) var(--md-sys-motion-easing-standard)
    var(--md-sys-motion-duration-short2);
}

.nav--container-is_open .nav--handle {
  opacity: 0;
  visibility: hidden;
  transition:
    opacity var(--md-sys-motion-duration-short2) var(--md-sys-motion-easing-standard),
    visibility 1ms linear 150ms;
}

/* Scoped to .nav--scroller so it outranks the destination-pill rules below, which also match this link. */
.nav--scroller .nav--title {
  display: block;
  flex: none;
  padding: 16px 24px;
  margin-bottom: 8px;
  border-radius: 0;
  border-bottom: 1px solid var(--md-sys-color-outline-variant);
}

.nav--title svg {
  display: block;
  max-width: 100%;
  margin: 0 auto;
}

.nav--badge {
  margin: -8px 0 12px;
  text-align: center;
}

.nav--scroller {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  overflow-x: hidden;
}

.nav--overlay {
  position: fixed;
  inset: 0;
  background-color: var(--pm-color-scrim-overlay, rgba(0, 0, 0, 0.32));
  visibility: hidden;
  opacity: 0;
  z-index: 28;
  will-change: opacity;
  transition:
    opacity var(--md-sys-motion-duration-short2) var(--md-sys-motion-easing-standard),
    visibility 1ms linear 150ms;
}

.layout-nav_open .nav--overlay {
  opacity: 1;
  visibility: visible;
  transition:
    opacity var(--md-sys-motion-duration-short2) var(--md-sys-motion-easing-standard),
    visibility 1ms linear;
}

.nav--links {
  flex: none;
  padding: 0 12px;
  margin: 0;
  list-style: none;
}

.nav--links li {
  position: relative;
}

.nav--links li + li:not(.nav--link-sub) {
  margin-top: 4px;
}

/* Every destination is a 56px pill (48px for a sub-destination), so each is a comfortable touch target. */
.nav--scroller a {
  display: flex;
  align-items: center;
  box-sizing: border-box;
  min-height: 56px;
  padding: 0 24px;
  border-radius: var(--md-sys-shape-corner-full);
  text-decoration: none;
  color: var(--md-sys-color-on-surface-variant);
  font-family: var(--md-sys-typescale-label-large-font);
  font-size: var(--md-sys-typescale-label-large-size);
  font-weight: var(--md-sys-typescale-label-large-weight);
  letter-spacing: var(--md-sys-typescale-label-large-tracking, normal);
  will-change: background-color;
  transition:
    background-color var(--md-sys-motion-duration-short3) var(--md-sys-motion-easing-standard),
    color var(--md-sys-motion-duration-short3) var(--md-sys-motion-easing-standard);
}

.nav--scroller a.nav--link-icon {
  padding-left: 16px;
  gap: 12px;
}

.nav--scroller a .material-icons {
  flex: none;
  --pm-icon-size: 24px;
}

/* The active destination is the secondary-container pill, with a filled icon. */
.nav--scroller li.nav--link-active a {
  background-color: var(--md-sys-color-secondary-container);
  color: var(--md-sys-color-on-secondary-container);
}

.nav--scroller li.nav--link-active a .material-icons {
  --pm-icon-fill: 1;
}

@media (hover: hover) {
  .nav--scroller a:hover {
    background-color: color-mix(
      in srgb,
      var(--md-sys-color-on-surface) calc(var(--md-sys-state-hover-state-layer-opacity) * 100%),
      transparent
    );
  }

  .nav--scroller li.nav--link-active a:hover {
    background-color: color-mix(
      in srgb,
      var(--md-sys-color-on-secondary-container) calc(var(--md-sys-state-hover-state-layer-opacity) * 100%),
      var(--md-sys-color-secondary-container)
    );
  }
}

.nav--scroller a:focus-visible {
  outline: var(--pm-focus-ring-width) solid var(--pm-focus-ring-color);
  outline-offset: var(--pm-focus-ring-offset);
}

.nav--scroller li.nav--link-sub a {
  min-height: 48px;
  padding-left: 56px;
  font-family: var(--md-sys-typescale-label-medium-font);
  font-size: var(--md-sys-typescale-label-medium-size);
}

.nav--scroller li.nav--link-sub a .material-icons {
  --pm-icon-size: 20px;
}

/* Contact Us, Accessibility and Privacy Policy stay at the foot of the drawer, whatever its height. */
.nav--bottom_links {
  position: sticky;
  bottom: 0;
  margin-top: auto;
  padding: 8px 12px;
  border-top: 1px solid var(--md-sys-color-outline-variant);
  background-color: var(--md-sys-color-surface-container-low);
}

.nav--bottom_links a {
  min-height: 48px;
  font-family: var(--md-sys-typescale-label-medium-font);
  font-size: var(--md-sys-typescale-label-medium-size);
}

/* WIDESCREEN: a standard (permanently docked) drawer is not modal - no rounding, no scrim, no handle. */
@media (min-width: 1568px) {
  .nav--container {
    border-radius: 0;
    box-shadow: var(--md-sys-elevation-level1);
    transform: none;
  }

  .nav--handle {
    visibility: hidden;
  }

  .layout-nav_open .nav--overlay {
    opacity: 0;
    visibility: hidden;
  }
}

.nav_fade-enter-active,
.nav_fade-leave-active {
  transition: opacity 350ms cubic-bezier(0.4, 0, 0.2, 1);
}

.nav_fade-enter-from,
.nav_fade-leave-to {
  opacity: 0;
}

.nav_fade-leave-from,
.nav_fade-enter-to {
  opacity: 1;
}
</style>
